import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Box, Button, Text } from "@vinomofo/components";
import { FONT_FAMILIES } from "../../styles";
import config from "../../../config.json";
import useAuth from "../../hooks/use-auth";
import { navigate } from "gatsby";
import { trackHubspotEvent } from "../../utils/event-tracking";
import { loadHubspotScript } from "../../utils/hubspot-script-loader";

export const generateHubspotFormId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `hubspot-form-${timestamp}-${randomStr}`;
};

export interface HubspotFormConfig {
  portalId: string;
  formId: string;
  region: string;
  target: string;
  onFormSubmit?: (formData: Record<string, unknown>) => void;
  onFormReady?: (form: HTMLFormElement) => void;
  formInstanceId?: string;
}

export interface HubspotFormProps {
  showLoginButton?: boolean;
  className?: string;
  formId?: string;
}

declare global {
  interface Window {
    hbspt: {
      forms: {
        create: (config: HubspotFormConfig) => void;
      };
    };
  }
}

const { dmSans } = FONT_FAMILIES;

export const HubspotFormContainer = styled(Box)`
  margin: 20px auto;
  max-width: 400px;

  @media ${({ theme }) => theme.mq.max.mobile} {
    max-width: 90%;
    margin: 15px auto;
  }

  .hs-form-field > label {
    display: none;
  }

  .submitted-message {
    color: #fff;
    text-align: center;
    font-family: ${dmSans};
    margin: 20px 0;
    padding: 20px;
  }

  .hs-input {
    font-family: ${dmSans};
    padding: 15px 15px;
    border-radius: 35px;
    width: 100%;
    max-width: 400px;
    border: none;
    margin-bottom: 10px;
    font-size: 14px;
    @media ${({ theme }) => theme.mq.max.mobile} {
      padding: 12px 15px;
    }
  }

  .hs-button {
    font-family: ${dmSans};
    background-color: #fbc44e;
    color: #000;
    border: none;
    font-size: 14px;
    border-radius: 35px;
    padding: 15px 20px;
    font-weight: 700;
    cursor: pointer;
    margin-top: 10px;
    width: 100%;
    max-width: 400px;

    @media ${({ theme }) => theme.mq.max.mobile} {
      padding: 12px 20px;
    }
  }

  .hs-button:hover {
    background-color: #f3b93c;
  }

  .hs-error-msgs {
    font-family: ${dmSans};
    list-style: none;
    font-size: 13px;
    font-weight: 300;
    display: flex;
    padding: 5px;
    align-items: center;
    margin: 4px auto;
    max-width: 400px;
    background-color: rgba(207, 44, 48, 0.7);
    border-radius: 10px 0 10px 0;

    li {
      text-align: left;
      margin-left: 10px;
    }
  }

  .hs-error-msg {
    color: #ffffff;
    font-family: ${dmSans};
    display: flex;
    align-items: center;
  }
`;

export const LoginButton = styled(Button)`
  font-family: ${dmSans};
  background-color: transparent;
  color: #fff;
  border: 1px solid ${({ theme }) => theme.colors.liquidGold};
  border-radius: 35px;
  padding: 13px 20px;
  min-width: 200px;
  cursor: pointer;
  text-decoration: none;
  margin-top: 15px;

  &:hover {
    background-color: #fbc44ed6;
    color: ${({ theme }) => theme.colors.almostWhite};
  }
  @media ${({ theme }) => theme.mq.max.mobile} {
    min-width: 200px;
    padding: 12px 10px;
  }
`;

export const OrDividerText = styled(Text)`
  position: relative;
  text-align: center;
  margin: 20px auto 5px;
  color: #fff;
  font-family: ${dmSans};
  font-size: 14px;
`;

const HubspotForm = ({ showLoginButton = false, formId }: HubspotFormProps) => {
  const formRef = useRef(null);
  const { login, isAuthenticated, user } = useAuth();
  const containerId = generateHubspotFormId();
  const handleFormSubmit = (formData: Record<string, unknown>) => {
    const formElement = formData as HTMLFormElement;
    const formId = formElement.getAttribute("data-form-id") as string;
    const emailInput = formElement.querySelector(
      'input[name="email"]'
    ) as HTMLInputElement;
    const email = emailInput?.value;
    if (email) {
      trackHubspotEvent("Hubspot Form Submitted", "form", {
        formId,
        email,
      });
    }

    setTimeout(() => {
      if (isAuthenticated) {
        navigate("/whiskymofo");
      } else {
        login({ returnTo: "/whiskymofo" });
      }
    }, 4000);
  };

  useEffect(() => {
    let isMounted = true;

    const initializeForm = async () => {
      try {
        await loadHubspotScript();

        if (isMounted && window.hbspt) {
          window.hbspt.forms.create({
            portalId:
              config.HUBSPOT_PORTAL_ID[
                process.env
                  .GATSBY_VM_ENV as keyof typeof config.HUBSPOT_PORTAL_ID
              ],
            formId:
              formId ||
              config.WHISKYMOFO_HUBSPOT_FORM_ID[
                process.env
                  .GATSBY_VM_ENV as keyof typeof config.WHISKYMOFO_HUBSPOT_FORM_ID
              ],
            formInstanceId: `form-instance-${containerId}`,
            region: "na1",
            target: `#${containerId}`,
            onFormSubmit: handleFormSubmit,
            onFormReady: (form) => {
              if (isAuthenticated && user?.email) {
                const emailInput = form.querySelector(
                  'input[name="email"]'
                ) as HTMLInputElement;
                if (emailInput) {
                  emailInput.value = user.email;

                  const inputEvent = new Event("input", { bubbles: true });
                  emailInput.dispatchEvent(inputEvent);

                  const emailField = emailInput.closest(".hs-form-field");
                  if (emailField) {
                    (emailField as HTMLElement).style.display = "none";
                  }
                }
                const submitButton = form.querySelector(
                  ".hs-button"
                ) as HTMLButtonElement;
                if (submitButton) {
                  submitButton.value = "Unlock access";
                }
              }
            },
          });
        }
      } catch (error) {
        console.error("Failed to initialize Hubspot form:", error);
      }
    };

    initializeForm();

    return () => {
      isMounted = false;
    };
  }, [containerId, formId, isAuthenticated, user]);

  const handleLogin = () => {
    trackHubspotEvent("Hubspot Login Button Clicked", "login", {
      returnTo: "/whiskymofo",
    });
    login({ returnTo: "/whiskymofo" });
  };

  return (
    <>
      <HubspotFormContainer id={`${containerId}`} ref={formRef} />
      {showLoginButton && !isAuthenticated && (
        <>
          <OrDividerText>OR</OrDividerText>
          <Box>
            <LoginButton as="button" onClick={handleLogin}>
              Login
            </LoginButton>
          </Box>
        </>
      )}
    </>
  );
};

export { HubspotForm };
